import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  FiCamera,
  FiVideo,
  FiMic,
  FiSquare,
  FiX,
  FiRotateCw,
  FiFileText,
} from 'react-icons/fi';

/**
 * MediaCapture — the reusable shop-floor capture component.
 *
 * Photo / video are captured via a native `<input type="file" accept capture>`
 * (most reliable on mobile). Voice notes use MediaRecorder with a big mic
 * button, an elapsed timer and a playback preview; if MediaRecorder /
 * getUserMedia is unavailable the voice button is hidden and audio falls back
 * to a native file input. Selected files upload to `POST /api/staff-media`
 * (multipart, per-file progress) and the resulting attachment objects are
 * returned to the parent via `onChange`.
 *
 * Props:
 *   allow    — array of allowed kinds: ['image','video','audio'] (default all)
 *   max      — max number of attachments (default 10)
 *   value    — current attachment array (controlled)
 *   onChange — (attachments[]) => void  fired whenever the list changes
 */

const ACCEPT = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
};

// Detect MediaRecorder + getUserMedia support once (guarded for test/SSR envs).
const recorderSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

let uidCounter = 0;
const nextId = () => `mc-${Date.now()}-${(uidCounter += 1)}`;

const formatTimer = (secs) => {
  const m = Math.floor(secs / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
};

const MediaCapture = ({
  allow = ['image', 'video', 'audio'],
  max = 10,
  value = [],
  onChange,
}) => {
  const { t } = useTranslation();

  // Local in-flight items keyed by id: { id, status:'uploading'|'error', progress, name }
  const [pending, setPending] = useState([]);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recordError, setRecordError] = useState(null);

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const total = value.length + pending.filter((p) => p.status === 'uploading').length;
  const atMax = total >= max;

  const showVoice = allow.includes('audio') && recorderSupported();

  const emit = useCallback(
    (next) => {
      if (onChange) onChange(next);
    },
    [onChange]
  );

  // ---- Upload a single File via POST /api/staff-media (multipart, progress) ----
  const uploadFile = useCallback(
    (file) => {
      const id = nextId();
      setPending((prev) => [
        ...prev,
        { id, status: 'uploading', progress: 0, name: file.name, file },
      ]);

      const form = new FormData();
      form.append('files', file);

      axios
        .post('/api/staff-media', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded * 100) / e.total);
            setPending((prev) =>
              prev.map((p) => (p.id === id ? { ...p, progress: pct } : p))
            );
          },
        })
        .then((res) => {
          const attachments = Array.isArray(res.data) ? res.data : [res.data];
          setPending((prev) => prev.filter((p) => p.id !== id));
          emit([...value, ...attachments]);
        })
        .catch((err) => {
          const tooBig =
            err?.response?.status === 413 ||
            err?.response?.data?.error === 'FILE_TOO_BIG';
          setPending((prev) =>
            prev.map((p) =>
              p.id === id
                ? { ...p, status: 'error', tooBig }
                : p
            )
          );
        });
    },
    [emit, value]
  );

  const handleFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      const room = Math.max(0, max - total);
      files.slice(0, room).forEach((f) => uploadFile(f));
    },
    [max, total, uploadFile]
  );

  const onInputChange = (e) => {
    handleFiles(e.target.files);
    // reset so picking the same file again re-fires change
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    emit(next);
  };

  const retryPending = (id) => {
    const item = pending.find((p) => p.id === id);
    if (!item) return;
    setPending((prev) => prev.filter((p) => p.id !== id));
    if (item.file) uploadFile(item.file);
  };

  const dismissPending = (id) => {
    setPending((prev) => prev.filter((p) => p.id !== id));
  };

  // ---- Voice recording (MediaRecorder) ----
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    setRecordError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new window.MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const mime = recorder.mimeType || 'audio/webm';
        const ext = mime.includes('mp4') || mime.includes('m4a') ? 'm4a' : 'webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mime });
        uploadFile(file);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      setRecordError(t('staff.capture.notSupported'));
    }
  };

  const stopRecording = () => {
    stopTimer();
    setRecording(false);
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  };

  // Cleanup on unmount.
  useEffect(
    () => () => {
      stopTimer();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    },
    []
  );

  const renderThumb = (att, idx) => {
    const isImage = att.type === 'image';
    const isVideo = att.type === 'video';
    return (
      <div
        key={`att-${idx}`}
        data-testid="media-chip"
        className="relative w-20 h-20 rounded-xl overflow-hidden border border-edge bg-surface-alt flex items-center justify-center shrink-0"
      >
        {isImage ? (
          <img src={att.url} alt={att.filename || ''} className="w-full h-full object-cover" />
        ) : isVideo ? (
          <FiVideo size={28} className="text-content-muted" />
        ) : (
          <FiMic size={28} className="text-content-muted" />
        )}
        <button
          type="button"
          onClick={() => removeAttachment(idx)}
          aria-label={t('staff.capture.remove')}
          className="absolute top-0.5 end-0.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
        >
          <FiX size={14} />
        </button>
      </div>
    );
  };

  const renderPending = (p) => (
    <div
      key={p.id}
      data-testid="media-pending"
      className="relative w-20 h-20 rounded-xl overflow-hidden border border-edge bg-surface-alt flex flex-col items-center justify-center shrink-0 p-1"
    >
      {p.status === 'uploading' ? (
        <>
          <FiFileText size={22} className="text-content-muted" />
          <span className="text-[10px] text-content-muted mt-1">
            {p.progress}%
          </span>
        </>
      ) : (
        <>
          <span className="text-[10px] text-red-600 dark:text-red-400 text-center leading-tight">
            {p.tooBig ? t('staff.capture.tooBig') : t('staff.capture.uploadFailed')}
          </span>
          <div className="flex gap-1 mt-1">
            <button
              type="button"
              onClick={() => retryPending(p.id)}
              aria-label={t('staff.capture.retry')}
              className="w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center"
            >
              <FiRotateCw size={12} />
            </button>
            <button
              type="button"
              onClick={() => dismissPending(p.id)}
              aria-label={t('staff.capture.remove')}
              className="w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center"
            >
              <FiX size={12} />
            </button>
          </div>
        </>
      )}
    </div>
  );

  const BigButton = ({ onClick, icon: Icon, label, disabled, testid }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="flex-1 min-w-[88px] min-h-[56px] flex flex-col items-center justify-center gap-1 rounded-2xl border border-edge bg-surface-alt text-content font-semibold hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon size={24} className="shrink-0" />
      <span className="text-sm">{label}</span>
    </button>
  );

  return (
    <div className="space-y-3" data-testid="media-capture">
      {/* Capture buttons */}
      <div className="flex gap-2 flex-wrap">
        {allow.includes('image') && (
          <>
            <BigButton
              testid="capture-photo"
              onClick={() => photoInputRef.current && photoInputRef.current.click()}
              icon={FiCamera}
              label={t('staff.capture.photo')}
              disabled={atMax}
            />
            <input
              ref={photoInputRef}
              type="file"
              accept={ACCEPT.image}
              capture="environment"
              multiple
              className="hidden"
              onChange={onInputChange}
              data-testid="input-photo"
            />
          </>
        )}

        {allow.includes('video') && (
          <>
            <BigButton
              testid="capture-video"
              onClick={() => videoInputRef.current && videoInputRef.current.click()}
              icon={FiVideo}
              label={t('staff.capture.video')}
              disabled={atMax}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept={ACCEPT.video}
              capture="environment"
              className="hidden"
              onChange={onInputChange}
              data-testid="input-video"
            />
          </>
        )}

        {allow.includes('audio') && showVoice && (
          <BigButton
            testid="capture-voice"
            onClick={recording ? stopRecording : startRecording}
            icon={recording ? FiSquare : FiMic}
            label={recording ? t('staff.capture.stop') : t('staff.capture.voice')}
            disabled={atMax && !recording}
          />
        )}

        {/* Fallback: audio allowed but no recorder support → native file input */}
        {allow.includes('audio') && !showVoice && (
          <>
            <BigButton
              testid="capture-voice-fallback"
              onClick={() => audioInputRef.current && audioInputRef.current.click()}
              icon={FiMic}
              label={t('staff.capture.voice')}
              disabled={atMax}
            />
            <input
              ref={audioInputRef}
              type="file"
              accept={ACCEPT.audio}
              className="hidden"
              onChange={onInputChange}
              data-testid="input-audio"
            />
          </>
        )}
      </div>

      {/* Recording indicator */}
      {recording && (
        <div
          data-testid="recording-indicator"
          className="flex items-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400"
        >
          <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span>{t('staff.capture.recording')}</span>
          <span className="tabular-nums">{formatTimer(elapsed)}</span>
        </div>
      )}

      {recordError && (
        <p className="text-xs text-red-600 dark:text-red-400">{recordError}</p>
      )}

      {/* Thumbnails / chips */}
      {(value.length > 0 || pending.length > 0) && (
        <div className="flex gap-2 flex-wrap">
          {value.map(renderThumb)}
          {pending.map(renderPending)}
        </div>
      )}
    </div>
  );
};

export default MediaCapture;
